<script lang="ts" setup>
  import { useApi } from '@/composables/use-api.ts';
  import { router } from '@/routes/router.ts';
  import { ApiFlexError } from '@/services/api/api-error.ts';
  import { convertSecondsToHoursAndMinutes } from '@/services/approval-application-svc.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { ApprovalStatusMap, useApprovalStore } from '@/stores/approval.ts';
  import { useThemeStore } from '@/stores/theme';
  import { showBackEndFlexErrorMessage } from '@/utils/error-message-helper.ts';
  import dayjs from 'dayjs';
  import { onMounted, ref } from 'vue';

  // path variable
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
  });

  const themeStore = useThemeStore();
  const { approval: approvalApi } = useApi();
  const approvalStore = useApprovalStore();
  const applicationId: number = Number(props.id);
  const application = isConfirmDetail()
    ? approvalStore.getMyConfirmApprovalApplication(applicationId)
    : approvalStore.getMyPendingApprovalApplication(applicationId);

  const agreeing = ref<boolean>(false);
  const disagreeing = ref<boolean>(false);
  const formInputs = {
    comment: '',
  };

  async function agreeAsync() {
    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          agreeing.value = status;
        },
        async () => {
          await approvalApi.agreeAsync(
            application?.approvalLine?.id!,
            formInputs.comment
          );
          router.back();
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '결재 승인 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
  }

  async function disagreeAsync() {
    if (!formInputs.comment) {
      alert('반려 사유를 입력해주세요.');
      return;
    }
    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          disagreeing.value = status;
        },
        async () => {
          await approvalApi.disagreeAsync(
            application?.approvalLine?.id!,
            formInputs.comment
          );
          router.back();
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '결재 승인 목록 조회 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
  }

  function isConfirmDetail() {
    return router.currentRoute.value.name == 'ApprovalConfirmDetail';
  }

  onMounted(() => {
    console.log('approvalApplicationDetail onMounted');
  });
</script>

<template>
  <DefaultLayout
    :title="
      !isConfirmDetail()
        ? '승인할 신청서 목록 / 상세'
        : '완료된 신청서 목록 / 상세'
    "
    go-back>
    <div
      class="border-slate-200 bg-base-200 card card-compact card-bordered w-4/5 mx-auto"
      :class="{
        'bg-neutral border-slate-700': themeStore.isDark,
      }">
      <div class="card-body gap-4">
        <div>
          <h4>신청자</h4>
          <p class="pt-1 font-bold">
            {{ application?.employee.organization.name }}
            <b>{{ application?.employee.name }}</b>
          </p>
        </div>

        <div>
          <h4>신청 유형</h4>
          <p class="pt-1 font-bold">
            {{ application?.approvalDocument?.name }}
          </p>
        </div>

        <div>
          <h4>상태</h4>
          <p class="pt-1 font-bold">
            {{ ApprovalStatusMap[application?.status ?? 'REQUEST'] }}
          </p>
        </div>

        <div>
          <h4>근무일</h4>
          <p class="pt-1 font-bold">
            {{ dayjs(application?.date).format('YYYY-MM-DD') }}
          </p>
        </div>

        <div>
          <h4>주간 근무 시간</h4>
          <p class="pt-1 font-bold">
            {{ convertSecondsToHoursAndMinutes(application?.dayTime ?? 0) }}
          </p>
        </div>

        <div>
          <h4>야간 근무 시간</h4>
          <p class="pt-1 font-bold">
            {{ convertSecondsToHoursAndMinutes(application?.nightTime ?? 0) }}
          </p>
        </div>

        <div>
          <h4>신청 사유</h4>
          <p class="pt-1 font-bold">{{ application?.comment }}</p>
        </div>

        <div v-if="application?.status == 'REJECT'">
          <h4>반려 사유</h4>
          <p class="pt-1 font-bold">{{ application?.approvalLine?.comment }}</p>
        </div>
      </div>
    </div>

    <div v-if="!isConfirmDetail() && application?.status == 'REQUEST'">
      <div class="flex items-center justify-center mt-5 join">
        <button
          class="w-24 join-item btn btn-error"
          :disabled="agreeing || disagreeing"
          onclick="disagree_application.showModal()">
          <span
            v-if="disagreeing"
            class="loading loading-spinner" />
          반려
        </button>
        <button
          v-if="application && !dayjs(application?.date).isAfter(dayjs())"
          :disabled="agreeing || disagreeing"
          class="w-32 join-item btn"
          @click="
            router.push({
              name: 'AttendanceDayView',
              params: {
                targetDate: dayjs(application?.date).format('YYYY-MM-DD'),
                empNo: application?.empNo,
              },
            })
          ">
          근무 기록 열람
        </button>
        <button
          class="w-24 join-item btn btn-primary"
          :disabled="agreeing || disagreeing"
          @click="agreeAsync">
          <span
            v-if="agreeing"
            class="loading loading-spinner" />
          승인
        </button>
      </div>
    </div>
    <div
      v-else
      class="flex items-center justify-center mt-5 join">
      <button
        v-if="application && !dayjs(application?.date).isAfter(dayjs())"
        :disabled="agreeing || disagreeing"
        class="w-32 join-item btn"
        @click="
          router.push({
            name: 'AttendanceDayView',
            params: {
              targetDate: dayjs(application?.date).format('YYYY-MM-DD'),
              empNo: application?.empNo,
            },
          })
        ">
        근무 기록 열람
      </button>
    </div>
  </DefaultLayout>

  <AppModal
    id="disagree_application"
    has-close-x>
    <h3 class="pt-2 pb-2">반려 사유</h3>
    <textarea
      v-model="formInputs.comment"
      class="w-full textarea textarea-bordered"
      placeholder="코멘트를 입력하세요." />
    <template #action>
      <button
        class="mt-2 btn btn-block btn-error"
        @click="disagreeAsync">
        반려
      </button>
    </template>
  </AppModal>
</template>
